/* Static descriptor of the gateway's overridable settings groups — a faithful
 * mirror of ProxySettings (ai21_coding_gateway/proxy/settings.py) restricted to
 * OVERRIDABLE_GROUPS. The config editor renders friendly controls from this, so
 * users never touch raw JSON. Keep in sync with the gateway settings.
 *
 * Deliberately a subset: product-specific overridable groups that are no-ops on
 * Claude Code traffic (the `gitlab_cr_*` code-review payload levers) are omitted
 * so this dashboard stays product-neutral — see the "Agent stages" block below.
 *
 * Each transformer is one settings group with an `enabled` toggle plus typed
 * params; the `gateway` group is the pipeline master switch. Transformers are
 * sorted into families ("similar transformers") and tagged by safety.
 *
 * This descriptor carries the *presentation* metadata the gateway doesn't model
 * (labels, blurbs, families, safety, advanced disclosure) plus baseline default
 * values used as a fallback. At runtime the live, deployment-resolved defaults
 * are fetched from GET /settings/configs/schema and overlaid via `mergeSchema`, so
 * what the editor shows tracks the gateway rather than these literals. */

import type { ConfigSchema } from "./types";

export type ConfigFieldKind =
  "bool" | "int" | "float" | "string" | "enum" | "stringList";

export interface ConfigField {
  key: string;
  label: string;
  kind: ConfigFieldKind;
  default: boolean | number | string | string[];
  min?: number;
  max?: number;
  step?: number;
  options?: readonly string[];
  help?: string;
  /** Hidden behind an "Advanced" disclosure (rarely-tuned / measurement knobs). */
  advanced?: boolean;
}

/** Risk posture, surfaced as a badge and used by the "lossless only" preset.
 *  `experimental` outranks the rest: not yet proven, whatever it costs. */
export type Safety = "lossless" | "lossy" | "routing" | "experimental";

export type FamilyKey =
  "tool_results" | "tool_defs" | "routing_cache" | "prompt" | "stages";

export interface FamilySpec {
  key: FamilyKey;
  label: string;
  blurb: string;
}

export interface TransformerSpec {
  /** overrides group key (matches the gateway settings group name). */
  key: string;
  label: string;
  blurb: string;
  family: FamilyKey;
  safety: Safety;
  enabledDefault: boolean;
  /** Params other than `enabled`. */
  fields: ConfigField[];
}

export const FAMILIES: FamilySpec[] = [
  {
    key: "tool_results",
    label: "Tool results",
    blurb:
      "Compress and clean tool output — the biggest, most compressible mass.",
  },
  {
    key: "tool_defs",
    label: "Tool definitions",
    blurb: "Shrink the cached tools prefix; the saving repeats every turn.",
  },
  {
    key: "routing_cache",
    label: "Routing & cache",
    blurb:
      "Route cheap turns to a smaller model and keep the prompt cache warm.",
  },
  {
    key: "prompt",
    label: "Prompt & output",
    blurb: "Trim oversized inputs and steer the model toward terser output.",
  },
  {
    key: "stages",
    label: "Agent stages",
    blurb:
      "Levers for multi-stage agents: route mechanical stages to a smaller model and give the commit stage a thinking budget. Defaults are tuned for code-review agents.",
  },
];

/** The pipeline master switch (settings group `gateway`). Not a transformer. */
export interface GatewayGroupSpec {
  key: string;
  label: string;
  enabledDefault: boolean;
  fields: ConfigField[];
}

export const GATEWAY_GROUP: GatewayGroupSpec = {
  key: "gateway",
  label: "Transformer pipeline",
  enabledDefault: true,
  fields: [
    {
      key: "strip_cache_control",
      label: "Strip cache_control (measurement only)",
      kind: "bool",
      default: false,
      advanced: true,
      help: "Force both A/B arms to pay full price. For clean measurement runs only.",
    },
  ] as ConfigField[],
};

export const TRANSFORMERS: TransformerSpec[] = [
  // ---- Tool results --------------------------------------------------------
  {
    key: "rtk",
    label: "RTK compression",
    blurb: "Compress git / grep / find / ls output via the rtk binary.",
    family: "tool_results",
    safety: "lossless",
    enabledDefault: true,
    fields: [
      {
        key: "min_bytes",
        label: "Minimum size",
        kind: "int",
        default: 200,
        min: 0,
        help: "Skip results smaller than this many bytes.",
      },
      {
        key: "compress_readback",
        label: "Compress read-back diffs & listings",
        kind: "bool",
        default: false,
        advanced: true,
        help: "Lossy: can make the agent re-run commands. Measurement only.",
      },
      {
        key: "allow_nondeterministic",
        label: "Allow non-deterministic filters",
        kind: "bool",
        default: false,
        advanced: true,
        help: "Permits mypy/tsc filters that can bust the prompt cache.",
      },
      {
        key: "binary_path",
        label: "rtk binary path",
        kind: "string",
        default: "rtk",
        advanced: true,
      },
    ],
  },
  {
    key: "local_compression",
    label: "Local compression",
    blurb: "In-process compaction for ls / gh / kubectl / cat-style output.",
    family: "tool_results",
    safety: "lossless",
    enabledDefault: true,
    fields: [
      {
        key: "min_bytes",
        label: "Minimum size",
        kind: "int",
        default: 200,
        min: 0,
        help: "Skip results smaller than this many bytes.",
      },
    ],
  },
  {
    key: "tool_result_sanitization",
    label: "Result sanitization",
    blurb: "Strip ANSI escape codes and repeated wrapper boilerplate.",
    family: "tool_results",
    safety: "lossless",
    enabledDefault: false,
    fields: [
      {
        key: "strip_ansi",
        label: "Strip ANSI / terminal escapes",
        kind: "bool",
        default: true,
      },
      {
        key: "strip_boilerplate",
        label: "Elide repeated wrapper lines",
        kind: "bool",
        default: true,
      },
    ],
  },
  {
    key: "tool_result_truncation",
    label: "Result truncation",
    blurb: "Trim the middle of oversized results, keeping head + tail.",
    family: "tool_results",
    safety: "lossy",
    enabledDefault: false,
    fields: [
      {
        key: "max_lines",
        label: "Trigger over lines",
        kind: "int",
        default: 100,
        min: 10,
      },
      {
        key: "max_bytes",
        label: "Trigger over bytes",
        kind: "int",
        default: 6000,
        min: 500,
        step: 500,
      },
      {
        key: "head_lines",
        label: "Keep head lines",
        kind: "int",
        default: 40,
        min: 1,
      },
      {
        key: "tail_lines",
        label: "Keep tail lines",
        kind: "int",
        default: 20,
        min: 0,
      },
      {
        key: "exclude_read",
        label: "Never truncate Read output",
        kind: "bool",
        default: false,
        help: "Safer for edit accuracy, smaller saving.",
      },
    ],
  },
  {
    key: "ccr",
    label: "CCR (Compress-Cache-Retrieve)",
    blurb:
      "Aggressively compress oversized tool results, park originals in Redis, and inject an expand tool so the model retrieves only what it needs. Mutually exclusive with result truncation — CCR takes precedence when both are enabled.",
    family: "tool_results",
    safety: "lossy",
    enabledDefault: false,
    fields: [
      {
        key: "min_bytes",
        label: "Trigger over bytes",
        kind: "int",
        default: 6000,
        min: 500,
        step: 500,
        help: "Only CCR-compress a tool result at least this many bytes.",
      },
      {
        key: "head_lines",
        label: "Keep head lines",
        kind: "int",
        default: 20,
        min: 0,
        help: "Lines kept inline as a preview; the rest go to Redis.",
      },
      {
        key: "ttl_seconds",
        label: "Redis TTL (s)",
        kind: "int",
        default: 300,
        min: 1,
        advanced: true,
        help: "How long originals are held in Redis before expiry.",
      },
    ],
  },
  {
    key: "tool_result_dedup",
    label: "Result de-duplication",
    blurb: "Replace a repeated identical result with a short back-reference.",
    family: "tool_results",
    safety: "lossless",
    enabledDefault: false,
    fields: [
      {
        key: "min_bytes",
        label: "Minimum size",
        kind: "int",
        default: 200,
        min: 0,
        help: "Only dedup duplicates at least this many bytes.",
      },
    ],
  },
  // ---- Tool definitions ----------------------------------------------------
  {
    key: "tool_definition_compression",
    label: "Definition compression",
    blurb: "Losslessly shrink tool descriptions and schema boilerplate.",
    family: "tool_defs",
    safety: "lossless",
    enabledDefault: true,
    fields: [
      {
        key: "strip_schema_boilerplate",
        label: "Drop ignored schema annotations",
        kind: "bool",
        default: true,
      },
      {
        key: "collapse_whitespace",
        label: "Collapse whitespace in descriptions",
        kind: "bool",
        default: true,
      },
      {
        key: "strip_examples",
        label: "Drop example blocks",
        kind: "bool",
        default: false,
        help: "Lossy: removes model-visible prose; can shift tool selection.",
      },
      {
        key: "min_bytes",
        label: "Minimum tool size",
        kind: "int",
        default: 0,
        min: 0,
        advanced: true,
      },
    ],
  },
  {
    key: "tool_definition_slim",
    label: "Definition slimming",
    blurb: "Replace long-tail tool descriptions with a short budgeted stub.",
    family: "tool_defs",
    safety: "lossy",
    enabledDefault: false,
    fields: [
      {
        key: "description_budget_bytes",
        label: "Description budget (bytes)",
        kind: "int",
        default: 200,
        min: 0,
        step: 50,
      },
      {
        key: "protected_tools",
        label: "Never-slimmed tools",
        kind: "stringList",
        default: ["Bash", "Read", "Write", "Grep", "Edit", "Glob", "Task*"],
        help: "Exact names; a trailing * is a prefix wildcard.",
      },
    ],
  },
  // ---- Routing & cache -----------------------------------------------------
  {
    key: "routing",
    label: "Model routing",
    blurb:
      "Route eligible turns (sub-agents, simple main-loop) to a cheaper model.",
    family: "routing_cache",
    safety: "routing",
    enabledDefault: false,
    fields: [
      {
        key: "shadow",
        label: "Shadow mode (log only, don't reroute)",
        kind: "bool",
        default: true,
        help: "Evaluate rules and record the decision without changing the model.",
      },
      {
        key: "subagent_rule_enabled",
        label: "Sub-agent → Haiku rule",
        kind: "bool",
        default: true,
      },
      {
        key: "simple_main_loop_rule_enabled",
        label: "Simple main-loop → Haiku rule",
        kind: "bool",
        default: true,
      },
      {
        key: "simple_main_loop_route_stride",
        label: "Route stride (1 in N turns)",
        kind: "int",
        default: 1,
        min: 1,
        help: "Avoid adjacent cheap turns so the premium cache stays warm.",
      },
      {
        key: "simple_main_loop_max_messages",
        label: "Max messages",
        kind: "int",
        default: 20,
        min: 0,
        help: "Don't route once history grows beyond this.",
      },
      {
        key: "simple_main_loop_min_messages",
        label: "Min messages",
        kind: "int",
        default: 0,
        min: 0,
        help: "Protect the opening turns from routing.",
      },
      {
        key: "subagent_target_model",
        label: "Sub-agent target model",
        kind: "string",
        default: "claude-haiku-4-5",
        advanced: true,
      },
      {
        key: "simple_main_loop_target_model",
        label: "Main-loop target model",
        kind: "string",
        default: "claude-haiku-4-5",
        advanced: true,
      },
      {
        key: "simple_main_loop_thinking_lookback",
        label: "Thinking look-back",
        kind: "int",
        default: 0,
        min: 0,
        advanced: true,
      },
      {
        key: "simple_main_loop_ignore_thinking_gate",
        label: "Ignore thinking gate (eval only)",
        kind: "bool",
        default: false,
        advanced: true,
      },
    ],
  },
  {
    key: "cache_anchor",
    label: "Cache anchor",
    blurb:
      "Inject a stable 4th cache breakpoint so routed detours don't reset the cache.",
    family: "routing_cache",
    safety: "lossless",
    enabledDefault: false,
    fields: [
      {
        key: "stride",
        label: "Anchor stride (messages)",
        kind: "int",
        default: 24,
        min: 2,
      },
      {
        key: "tail_gap",
        label: "Tail gap (messages)",
        kind: "int",
        default: 2,
        min: 1,
        advanced: true,
      },
      {
        key: "min_messages",
        label: "Minimum messages",
        kind: "int",
        default: 4,
        min: 0,
        advanced: true,
      },
    ],
  },
  // ---- Prompt & output -----------------------------------------------------
  {
    key: "bash_input_compression",
    label: "Bash input compression",
    blurb:
      "Stub large inline-python in older Bash commands the agent has moved past.",
    family: "prompt",
    safety: "lossy",
    enabledDefault: false,
    fields: [
      {
        key: "min_bytes",
        label: "Minimum command size",
        kind: "int",
        default: 1500,
        min: 100,
        step: 100,
      },
      {
        key: "keep_last_turns",
        label: "Keep last N turns verbatim",
        kind: "int",
        default: 4,
        min: 0,
      },
    ],
  },
  {
    key: "skill_injection",
    label: "Output style (caveman)",
    blurb:
      "Append a terse-output directive to the system prompt to cut output tokens.",
    family: "prompt",
    safety: "lossy",
    enabledDefault: false,
    fields: [
      {
        key: "level",
        label: "Intensity",
        kind: "enum",
        default: "lite",
        options: ["lite", "full", "ultra"],
        help: "lite trims filler; ultra is maximum brevity (highest risk).",
      },
    ],
  },
  // Context imaging (gateway groups `pxpipe_prompt` / `pxpipe_tool_results`; the
  // `pxpipe_history` group also exists in the gateway but is intentionally not
  // surfaced here yet). Two cards across two families because it reshapes two
  // different places, but ONE mechanism: the renderer's engine settings
  // (model allow-list, page cache, render admission, image compression) are
  // deployment-level in the gateway and deliberately not overridable per config,
  // so no card exposes params — just the toggle.
  {
    key: "pxpipe_prompt",
    label: "Prompt imaging",
    blurb:
      "Pack system prompt and tools definitions into a compact image " +
      "representation to reduce input token overhead.",
    family: "prompt",
    safety: "experimental",
    enabledDefault: false,
    fields: [],
  },
  {
    key: "pxpipe_tool_results",
    label: "Tool result imaging",
    blurb:
      "Render oversized tool results as compact images. Requires prompt imaging.",
    family: "tool_results",
    safety: "experimental",
    enabledDefault: false,
    fields: [],
  },
  // ---- Agent stages --------------------------------------------------------
  // Generic multi-stage-agent levers (gateway groups `stage_model_route` /
  // `stage_thinking`); defaults target a code-review agent but the mechanism is
  // product-neutral. The gateway also ships payload-specific code-review levers
  // (`gitlab_cr_*`: file_window / diff_compact / file_trim / sys_hoist) that
  // are no-ops on Claude Code traffic — intentionally NOT surfaced here, so this
  // dashboard stays product-neutral. Don't re-add them when syncing the schema.
  {
    key: "stage_model_route",
    label: "Stage model routing",
    blurb:
      "Route a multi-stage agent's mechanical stages — those using only the listed cheap tools — to a smaller model. Reasoning and commit stages are never routed.",
    family: "stages",
    safety: "routing",
    enabledDefault: false,
    fields: [
      {
        key: "target_model",
        label: "Target model",
        kind: "string",
        default: "claude-haiku-4-5",
        advanced: true,
        help: "Model the mechanical stages are routed to.",
      },
      {
        key: "cheap_tools",
        label: "Mechanical-stage tools",
        kind: "stringList",
        default: ["list_repository_tree", "get_repository_file", "read_file"],
        advanced: true,
        help: "A request whose tools are a non-empty subset of these marks a mechanical stage and is routed; stages carrying any other tool, and no-tool reasoning stages, never are.",
      },
    ],
  },
  {
    key: "stage_thinking",
    label: "Stage thinking",
    blurb:
      "Give a marked stage a capped extended-thinking budget so the model reasons before committing its answer.",
    family: "stages",
    safety: "lossy",
    enabledDefault: false,
    fields: [
      {
        key: "budget_tokens",
        label: "Thinking budget",
        kind: "int",
        default: 3000,
        min: 1024,
        help: "Thinking budget ceiling (Anthropic min 1024); billed at output rate only for thinking actually used.",
      },
      {
        key: "min_output_tokens",
        label: "Output headroom",
        kind: "int",
        default: 4096,
        min: 256,
        advanced: true,
        help: "Headroom added above the thinking budget when bumping max_tokens.",
      },
      {
        key: "target_tools",
        label: "Marked-stage tools",
        kind: "stringList",
        default: ["post_duo_code_review"],
        advanced: true,
        help: "Tool names that mark the stage where thinking is enabled.",
      },
    ],
  },
];

export const SAFETY_LABEL: Record<Safety, string> = {
  lossless: "Lossless",
  lossy: "Lossy",
  routing: "Routing",
  experimental: "Experimental",
};

export function transformersInFamily(family: FamilyKey): TransformerSpec[] {
  return TRANSFORMERS.filter((t) => t.family === family);
}

export function transformerByKey(key: string): TransformerSpec | undefined {
  return TRANSFORMERS.find((t) => t.key === key);
}

// ---- effective schema (gateway defaults overlaid on presentation) ----------

/** The editor's working schema: the master group plus the transformers, with
 *  defaults/bounds/options resolved (from the gateway when available). */
export interface EffectiveSchema {
  gatewayGroup: GatewayGroupSpec;
  transformers: TransformerSpec[];
}

/** Presentation metadata with the static fallback defaults baked in. */
export const STATIC_SCHEMA: EffectiveSchema = {
  gatewayGroup: GATEWAY_GROUP,
  transformers: TRANSFORMERS,
};

/** The minimal group shape `mergeSchema` overlays onto; both the gateway group
 *  and a transformer spec satisfy it. */
interface GroupShape {
  key: string;
  enabledDefault: boolean;
  fields: ConfigField[];
}

/** Overlay one fetched group's resolved values onto a static spec, preserving
 *  the spec's presentation metadata. Unmatched fields keep their static value. */
function overlayGroup<T extends GroupShape>(
  spec: T,
  fetched: ConfigSchema["groups"][number] | undefined,
): T {
  if (!fetched) return spec;

  const byKey = new Map(fetched.fields.map((f) => [f.key, f]));
  return {
    ...spec,
    enabledDefault: fetched.enabled_default,
    fields: spec.fields.map((f) => {
      const g = byKey.get(f.key);
      if (!g) return f;

      return {
        ...f,
        default: g.default,
        min: g.minimum ?? f.min,
        max: g.maximum ?? f.max,
        options: g.options ?? f.options,
        help: f.help ?? g.description ?? undefined,
      };
    }),
  };
}

/** Overlay the gateway's resolved schema onto the static presentation metadata.
 *  Passing `null` (not yet loaded / fetch failed) returns the static fallback,
 *  so the editor always has a complete, usable schema. */
export function mergeSchema(fetched: ConfigSchema | null): EffectiveSchema {
  if (!fetched) return STATIC_SCHEMA;

  const byKey = new Map(fetched.groups.map((g) => [g.key, g]));
  return {
    gatewayGroup: overlayGroup(GATEWAY_GROUP, byKey.get(GATEWAY_GROUP.key)),
    transformers: TRANSFORMERS.map((t) => overlayGroup(t, byKey.get(t.key))),
  };
}

/** Serialize the static schema into the gateway's wire shape — the mock config
 *  client serves this so mock mode overlays to exactly the static defaults. */
export function staticConfigSchema(): ConfigSchema {
  const toGroup = (
    g: GatewayGroupSpec | TransformerSpec,
  ): ConfigSchema["groups"][number] => ({
    key: g.key,
    enabled_default: g.enabledDefault,
    fields: [
      { key: "enabled", kind: "bool", default: g.enabledDefault },
      ...g.fields.map((f) => ({
        key: f.key,
        kind: f.kind,
        default: f.default,
        description: f.help ?? null,
        options: f.options ? [...f.options] : null,
        minimum: f.min ?? null,
        maximum: f.max ?? null,
      })),
    ],
  });

  return { groups: [GATEWAY_GROUP, ...TRANSFORMERS].map(toGroup) };
}
